import React, { useState } from "react";
import { Avatar as MuiAvatar, Badge, IconButton, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";

import EditIcon from '@mui/icons-material/Edit';
import PersonIcon from '@mui/icons-material/Person';

import { InfegyUser, UserPreference } from "system/InfegyUser";
import AvatarImageSelector from "components/avatarImageSelector/AvatarImageSelector";
import APIRequest from "system/APIRequest";
import { useSnackbar } from "notistack";

export async function saveAvatar(image: string, user: InfegyUser): Promise<void> {
  user.setPreference(UserPreference.AVATAR, image);
  const body = {
    preferences: user.preferences
  };
  await APIRequest(`obj/user/${user.id}`, {}, {
    method: "PUT",
    body: JSON.stringify(body)
  });
}

interface AvatarProps {
  autoSave?: boolean;
  image?: string;
  initials?: string;
  isEditable?: boolean;
  onChange?(image: string): void;
  size?: number;
  /**
   * The user to display the Avatar for.
   */
  user?: InfegyUser;
}

export default function Avatar(props: AvatarProps): React.JSX.Element {
  const theme = useTheme();
  const { i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const [image, setImage] = useState<string>(props.user?.getPreference(UserPreference.AVATAR) || props.image || "");
  const [isEditing, setIsEditing] = useState(false);
  
  const autoSave = props.autoSave && props.user || false;
  const initials = props.user?.getInitials() || props.initials || "";
  const size = props.size || 32;

  let avatarComponent: React.JSX.Element;

  function handleEdit(): void {
    setIsEditing(true);
  };

  async function handleChange(image: string): Promise<void> {
    if (autoSave && props.user) {
      props.user.setPreference(UserPreference.AVATAR, image);
      const body = {
        preferences: props.user.preferences
      };
      await APIRequest(`obj/user/${props.user.id}`, {}, {
        method: "PUT",
        body: JSON.stringify(body)
      });
      enqueueSnackbar(i18n.t("avatar.image-saved"), { variant: "success" });
    }

    setImage(image);
    setIsEditing(false);
    props.onChange && props.onChange(image);
  }

  function handleCancel(): void {
    setIsEditing(false)
  }

  if (image) {
    // show the profile image
    avatarComponent = <MuiAvatar src={image} sx={{height: props.size, width: props.size}}><PersonIcon /></MuiAvatar>
  } else if (initials) {
    // show the initials
    const defaultColors = [
      theme.colors.namedColors.chart1,
      theme.colors.namedColors.chart2,
      theme.colors.namedColors.chart3,
      theme.colors.namedColors.chart4,
      theme.colors.namedColors.chart5,
      theme.colors.namedColors.chart6,
      theme.colors.namedColors.chart7,
      theme.colors.namedColors.chart8,
      theme.colors.namedColors.chart9
    ];
    let sum = 0;
    for (let i=0; i<initials.length; i++) {
      sum += initials.charCodeAt(i);
    }
    const defaultColor = defaultColors[sum % defaultColors.length];
    avatarComponent = <MuiAvatar sx={{height: size, width: size, backgroundColor: defaultColor}}>{initials}</MuiAvatar>;
  } else {
    // show the PersonIcon
    avatarComponent = <MuiAvatar sx={{height: size, width: size}}></MuiAvatar>
  }

  if (props.isEditable) {
    avatarComponent = (
      <>
        <IconButton sx={{ padding: 0 }} onClick={handleEdit}>
          <Badge
            anchorOrigin={{vertical: "bottom", horizontal: "right"}}
            badgeContent={
              <EditIcon
                sx={{
                  background: theme.colors.info.main,
                  borderRadius: 6,
                  color: "white",
                  marginBottom: 1,
                  marginRight: 1,
                  padding: "2px"
                }} />
            }
            sx={{height: "fit-content", width: "fit-content"}}
          >
            {avatarComponent}
          </Badge>
        </IconButton>
        {isEditing && 
          <AvatarImageSelector 
            image={image} 
            isOpen={isEditing} 
            onChange={handleChange} 
            onClose={handleCancel} />
        }
      </>
    )
  }

  return avatarComponent;
}
